import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link, useNavigate } from "react-router-dom";
import { signupSchema } from "../../schemas/signupSchema";
import {
  useAcceptUserAgreement,
  useAcceptVendorAgreement,
  useGetUserActiveAgreement,
  useGetVendorActiveAgreement,
  useUserSignUp,
} from "../../custom-hooks/queries";
import { useAuthStore } from "../../store/useAuthStore";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { FiLoader } from "react-icons/fi";
import { toast } from "react-toastify";

const SignUp = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(signupSchema),
    defaultValues: {
      isVendor: false,
      agreeToTerms: false,
    },
  });

  const isVendor = watch("isVendor");
  const { data: activeUserAgreement } = useGetUserActiveAgreement();

  const { mutate: acceptUserAgreement } = useAcceptUserAgreement();

  const navigate = useNavigate();
  const { mutate: userSignUp } = useUserSignUp();
  const { setAuth } = useAuthStore();

  const handleSignUp = (data) => {
    const userData = {
      first_name: data.firstName,
      last_name: data.lastName,
      email: data.email,
      phone: data.phone,
      password: data.password,
    };
    setLoading(true);

    userSignUp(userData, {
      onSuccess: (res) => {
        acceptUserAgreement(activeUserAgreement?.agreementId);
        const user = res?.data || res || {};
        setLoading(false);
        setAuth({
          token: res?.data?.token || res?.token,
          role: user?.role || "User",
          user: {
            id: user?.userId,
            firstName: user?.firstName || data.firstName,
            lastName: user?.lastName || data.lastName,
            name: `${user?.firstName || data.firstName} ${
              user?.lastName || data.lastName
            }`.trim(),
            email: user?.email || data.email,
            phone: user?.phone || data.phone,
            avatar: null,
          },
        });

        toast.success(
          "Account created successfully! Please verify your email.",
        );
        navigate("/otp", { state: { flow: "signup" }, replace: true });
      },
      onError: (error) => {
        setLoading(false);
        const errorMessage =
          error?.response?.data?.error_message ||
          error?.error_message ||
          "Failed to create account. Please try again.";
        toast.error(errorMessage);
      },
    });
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-white px-4 py-10">
      <div className="w-full max-w-[500px] border border-gray-200 shadow-sm rounded-2xl p-10 bg-white">
        <h1 className="text-3xl font-bold text-gray-900 text-center mb-2">
          Sign up
        </h1>
        <p className="text-gray-600 text-center mb-4">
          Join our platform and explore activities you'll love.
        </p>

        <div className="flex justify-center mb-8">
          <div className="w-[165px] h-[5px] bg-[#16BBD8] rounded"></div>
        </div>

        <form onSubmit={handleSubmit(handleSignUp)} className="space-y-5">
          {/* First + Last Name */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-800 mb-1">
                First Name
              </label>
              <input
                {...register("firstName")}
                type="text"
                placeholder="First Name"
                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#1FADC6] ${
                  errors.firstName ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.firstName && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.firstName.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-800 mb-1">
                Last Name
              </label>
              <input
                {...register("lastName")}
                type="text"
                placeholder="Last Name"
                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#1FADC6] ${
                  errors.lastName ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.lastName && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.lastName.message}
                </p>
              )}
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-1">
              Email
            </label>
            <input
              {...register("email")}
              type="email"
              placeholder="Email"
              className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#1FADC6] ${
                errors.email ? "border-red-500" : "border-gray-300"
              }`}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">
                {errors.email.message}
              </p>
            )}
          </div>

          {/* Password with Show / Hide */}
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-1">
              Password
            </label>

            <div className="relative">
              <input
                {...register("password")}
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                className={`w-full px-4 py-2.5 pr-10 border rounded-lg focus:ring-2 focus:ring-[#1FADC6] ${
                  errors.password ? "border-red-500" : "border-gray-300"
                }`}
              />

              <button
                type="button"
                onClick={() => setShowPassword((p) => !p)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            {errors.password && (
              <p className="mt-1 text-sm text-red-600">
                {errors.password.message}
              </p>
            )}
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-1">
              Phone <span className="text-gray-500">(optional)</span>
            </label>
            <input
              {...register("phone")}
              type="tel"
              placeholder="Phone"
              className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#1FADC6] ${
                errors.phone ? "border-red-500" : "border-gray-300"
              }`}
            />
            {errors.phone && (
              <p className="mt-1 text-sm text-red-600">
                {errors.phone.message}
              </p>
            )}
          </div>

          {/* Vendor Toggle */}
          <div className="flex items-center justify-between py-2">
            <label className="text-sm font-medium text-gray-800">
              Sign up as vendor?
            </label>

            <button
              type="button"
              onClick={() => navigate("/vendor/onboarding", { replace: true })}
              className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-300"
            >
              <span className="inline-block h-4 w-4 translate-x-1 rounded-full bg-white" />
            </button>
          </div>

          {/* Terms */}
          <div className="flex items-center">
            <input
              id="agreeToTerms"
              type="checkbox"
              {...register("agreeToTerms")}
              className="h-4 w-4 text-[#16BBD8] rounded border-gray-300"
            />
            <label
              htmlFor="agreeToTerms"
              className="ml-2 mt-1 text-[12px] text-gray-800 font-medium leading-none"
            >
              I agree to the{" "}
            </label>
            <a
              href={activeUserAgreement?.documentUrl || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-[#1FADC6] hover:text-[#00C2C7] text-[12px] font-medium ml-1"
              onClick={(e) => e.stopPropagation()}
            >
              Toucan User Agreement & Liability Release.
            </a>
          </div>

          {errors.agreeToTerms && (
            <p className="text-sm text-red-600 -mt-3">
              {errors.agreeToTerms.message}
            </p>
          )}

          <button
            type="submit"
            className="w-full flex items-center justify-center gap-2 bg-[#16BBD8] hover:bg-[#1FADC6] text-white font-medium py-3 px-4 rounded-lg transition-colors"
          >
            {loading && <FiLoader className="animate-spin" />}
            <span>Create Account</span>
          </button>

          <p className="text-center text-sm text-gray-600">
            Already have an account?{" "}
            <Link to="/signin" className="text-[#00C2C7] font-medium">
              Sign In
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default SignUp;
